import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, LogOut, Mail, Package, User, KeyRound, Gift } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import {
  getCustomerToken, setCustomerToken, clearCustomerToken,
  accountGet, accountPost, accountPut, refreshWishlist,
} from "@/lib/account";
import { useSEO } from "@/lib/seo";
import { displayTitle } from "@/lib/title";
import { apiError, money, fmtDate } from "@/lib/admin";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-[#E8DCC4] rounded-sm px-4 py-3 text-base text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#C9A227] transition-colors duration-300";

function MarketingToggle({ initial }) {
  const [optIn, setOptIn] = useState(initial);
  const [busy, setBusy] = useState(false);
  const save = async (next) => {
    setBusy(true);
    try {
      await accountPut("/account/marketing", { opt_in: next });
      setOptIn(next);
      toast.success(next ? "You're on the list — occasional offers, never spam" : "You're off the marketing list");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <label className="flex items-start gap-3 cursor-pointer" data-testid="account-marketing-toggle">
        <input
          type="checkbox"
          checked={optIn}
          disabled={busy}
          onChange={(e) => save(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-[#C9A227]"
          data-testid="account-marketing-checkbox"
        />
        <span className="text-sm text-neutral-600 leading-relaxed">
          Occasional offers and new arrivals by email. Order, shipping and refund emails are always sent —
          they are part of your purchase, not marketing.
        </span>
      </label>
      <p className="text-xs text-neutral-400 mt-3">{optIn ? "Subscribed to marketing emails." : "Not subscribed to marketing emails."}</p>
    </div>
  );
}

function AuthForms({ onAuth }) {
  const [mode, setMode] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = mode === "register"
        ? await axios.post(`${API}/account/register`, { name, email, password, marketing_opt_in: marketingOptIn })
        : await axios.post(`${API}/account/login`, { email, password });
      setCustomerToken(data.token);
      await refreshWishlist();
      toast.success(mode === "register" ? "Welcome to Gold Time — your 10% code is ready below" : "Welcome back");
      onAuth();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white border border-[#E8DCC4] rounded-sm p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="account-auth-card">
      <div className="flex gap-2 mb-6">
        {["register", "login"].map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            data-testid={`account-mode-${m}`}
            className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-colors duration-300 ${
              mode === m ? "bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900" : "border border-[#E8DCC4] text-neutral-600"
            }`}
          >
            {m === "register" ? "Create account" : "Sign in"}
          </button>
        ))}
      </div>
      {mode === "register" && (
        <p className="text-sm text-[#A8871B] bg-[#C9A227]/10 border border-[#C9A227]/25 rounded-sm px-4 py-3 mb-4" data-testid="account-welcome-note">
          Create your account and get <strong>10% off your first purchase</strong>.
        </p>
      )}
      <form onSubmit={submit} className="space-y-3">
        {mode === "register" && (
          <input required placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="account-name" />
        )}
        <input required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="account-email" />
        <input required type="password" minLength={8} placeholder="Password (8+ characters)" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="account-password" />
        {mode === "register" && (
          <label className="flex items-start gap-3 text-left cursor-pointer px-1 py-1" data-testid="register-marketing-consent">
            <input
              type="checkbox"
              checked={marketingOptIn}
              onChange={(e) => setMarketingOptIn(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#C9A227]"
              data-testid="register-marketing-checkbox"
            />
            <span className="text-xs text-neutral-500 leading-relaxed">
              Yes — email me occasional offers and new arrivals from Gold Time Jewelry. Order and shipping emails are
              sent regardless; you can change this anytime in your account.
            </span>
          </label>
        )}
        <button
          type="submit" disabled={busy}
          className="w-full rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold py-3.5 hover:from-[#F0D988] hover:to-[#D4AF37] transition-colors duration-300 disabled:opacity-60"
          data-testid="account-submit"
        >
          {busy ? "One moment…" : mode === "register" ? "Create my account" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

export default function AccountPage() {
  const [me, setMe] = useState(null);
  useSEO({ title: "Your Account | Gold Time Jewelry", canonicalPath: "/account", noindex: true });
  const [orders, setOrders] = useState(null);
  const [checked, setChecked] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [pw, setPw] = useState({ current: "", new: "" });
  const navigate = useNavigate();

  const load = async () => {
    if (!getCustomerToken()) { setChecked(true); return; }
    try {
      const [profile, ords] = await Promise.all([accountGet("/account/me"), accountGet("/account/orders")]);
      setMe(profile);
      setName(profile.name || "");
      setPhone(profile.phone || "");
      setOrders(ords.items || []);
    } catch {
      clearCustomerToken();
      setMe(null);
    } finally {
      setChecked(true);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => {
    clearCustomerToken();
    setMe(null);
    refreshWishlist();
    navigate("/account");
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await accountPut("/account/profile", { name, phone });
      toast.success("Profile saved");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await accountPost("/account/change-password", pw);
      toast.success("Password updated");
      setPw({ current: "", new: "" });
    } catch (err) { toast.error(apiError(err)); }
  };

  const removeWish = async (sku) => {
    try {
      const { accountDelete } = await import("@/lib/account");
      await accountDelete(`/account/wishlist/${encodeURIComponent(sku)}`);
      await refreshWishlist();
      load();
    } catch { toast.error("Couldn't remove that piece"); }
  };

  return (
    <div className="min-h-screen bg-[#F5F3EC]">
      <Header />
      <main className="pt-[164px] lg:pt-[196px] pb-24 px-5 lg:px-10 max-w-5xl mx-auto" data-testid="account-page">
        <h1 className="font-serif text-3xl lg:text-4xl text-neutral-900 mb-2" data-testid="account-heading">
          {me ? `Hello, ${(me.name || "").split(" ")[0] || "friend"}` : "Your Gold Time Account"}
        </h1>
        <p className="text-sm text-neutral-500 mb-10">
          {me ? "Your profile, wishlist and orders — all in one place." : "Sign in or create an account to save pieces and check out faster."}
        </p>

        {!checked ? (
          <p className="text-neutral-500 text-sm">Loading…</p>
        ) : !me ? (
          <AuthForms onAuth={load} />
        ) : (
          <div className="space-y-6">
            {me.welcome_code && (
              <div className="bg-white border border-[#C9A227]/40 rounded-sm p-6 flex flex-wrap items-center justify-between gap-4" data-testid="account-welcome-code">
                <div className="flex items-center gap-3">
                  <Gift size={20} className="text-[#C9A227]" />
                  <div>
                    <p className="text-sm font-semibold text-neutral-900">Your 10% welcome code</p>
                    <p className="text-xs text-neutral-500">Enter it in the promo code box at checkout. One use.</p>
                  </div>
                </div>
                <button
                  onClick={() => { navigator.clipboard?.writeText(me.welcome_code); toast.success("Code copied"); }}
                  className="font-mono text-lg tracking-[0.2em] text-[#A8871B] font-semibold border border-dashed border-[#C9A227] rounded-sm px-4 py-2 hover:bg-[#C9A227]/10 transition-colors"
                  data-testid="welcome-code-copy"
                  title="Click to copy"
                >
                  {me.welcome_code}
                </button>
              </div>
            )}

            <section className="bg-white border border-[#E8DCC4] rounded-sm p-6" data-testid="account-wishlist">
              <h2 className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-4 flex items-center gap-2">
                <Heart size={14} className="text-[#C9A227]" /> Your wishlist
              </h2>
              {(me.wishlist || []).length === 0 ? (
                <p className="text-sm text-neutral-500">
                  Nothing saved yet — tap the heart on any piece in the <Link to="/catalog" className="text-[#A8871B] underline">catalog</Link> to keep it here.
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {me.wishlist.map((w) => (
                    <div key={w.sku} className="border border-[#EFE7D5] rounded-sm p-3 flex gap-3 items-center" data-testid={`wishlist-item-${w.sku.replace(/[^\w]/g, "-")}`}>
                      <Link to={w.url} className="flex gap-3 items-center min-w-0 flex-1 group">
                        {w.image && <img src={w.image} alt="" className="w-14 h-14 object-contain bg-white border border-[#EFE7D5] rounded-sm shrink-0" loading="lazy" />}
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-neutral-900 truncate group-hover:text-[#A8871B] transition-colors">{displayTitle({ seo_title: w.title })}</span>
                          <span className="block text-xs text-neutral-500">{money(w.price)} · {w.in_stock ? "In stock" : "Order-in"}</span>
                        </span>
                      </Link>
                      <button onClick={() => removeWish(w.sku)} className="text-neutral-400 hover:text-red-500 shrink-0 text-xs" data-testid={`wishlist-remove-${w.sku.replace(/[^\w]/g, "-")}`}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="bg-white border border-[#E8DCC4] rounded-sm p-6" data-testid="account-orders">
              <h2 className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-4 flex items-center gap-2">
                <Package size={14} className="text-[#C9A227]" /> Your orders
              </h2>
              {!orders || orders.length === 0 ? (
                <p className="text-sm text-neutral-500">No orders on this email yet — your completed purchases will appear here.</p>
              ) : (
                <ul className="space-y-3">
                  {orders.map((o) => (
                    <li key={o.order_id} className="flex items-center gap-3 border-b border-[#EFE7D5] pb-3 last:border-0 text-sm" data-testid={`account-order-${o.order_id.slice(0, 8)}`}>
                      {o.items?.[0]?.image && <img src={o.items[0].image} alt="" className="w-10 h-10 object-contain bg-white border border-[#EFE7D5] rounded-sm" loading="lazy" />}
                      <span className="flex-1 min-w-0 text-neutral-700 truncate">
                        {(o.items || []).map((i) => i.name).join(", ")}
                        <span className="text-neutral-500"> · {o.delivery === "pickup" ? `Pickup — ${o.showroom}` : "Shipped"}</span>
                        <span className="block text-[11px] text-neutral-500 mt-0.5" data-testid={`account-order-status-${o.order_id.slice(0, 8)}`}>
                          {(o.fulfillment_status || "processing").replace(/_/g, " ")}
                          {(o.shipments || []).length > 0 && (
                            <>
                              {" · "}
                              <a href={o.shipments[o.shipments.length - 1].tracking_url || o.track_url}
                                 target="_blank" rel="noreferrer" className="text-[#A8871B] underline"
                                 data-testid={`account-order-track-${o.order_id.slice(0, 8)}`}>
                                Track {o.shipments[o.shipments.length - 1].carrier}
                              </a>
                            </>
                          )}
                          {" · "}
                          <a href={o.track_url} className="text-[#A8871B] underline"
                             data-testid={`account-order-view-${o.order_id.slice(0, 8)}`}>View order</a>
                        </span>
                      </span>
                      <span className="text-neutral-600 shrink-0">{money(o.amount)} · {fmtDate(o.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <div className="grid md:grid-cols-2 gap-6">
              <section className="bg-white border border-[#E8DCC4] rounded-sm p-6" data-testid="account-marketing">
                <h2 className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-4 flex items-center gap-2">
                  <Mail size={14} className="text-[#C9A227]" /> Email preferences
                </h2>
                <MarketingToggle initial={!!me.marketing_opt_in} />
              </section>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <section className="bg-white border border-[#E8DCC4] rounded-sm p-6" data-testid="account-profile">
                <h2 className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-4 flex items-center gap-2">
                  <User size={14} className="text-[#C9A227]" /> Profile
                </h2>
                <form onSubmit={saveProfile} className="space-y-3">
                  <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={inputCls} data-testid="profile-name" />
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" className={inputCls} data-testid="profile-phone" />
                  <p className="text-xs text-neutral-500">{me.email}</p>
                  <button type="submit" className="rounded-full border border-[#C9A227] text-[#A8871B] text-sm font-semibold px-6 py-2.5 hover:bg-[#F5F3EC] transition-colors" data-testid="profile-save">
                    Save profile
                  </button>
                </form>
              </section>

              <section className="bg-white border border-[#E8DCC4] rounded-sm p-6" data-testid="account-password">
                <h2 className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-4 flex items-center gap-2">
                  <KeyRound size={14} className="text-[#C9A227]" /> Password
                </h2>
                <form onSubmit={changePassword} className="space-y-3">
                  <input required type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} placeholder="Current password" className={inputCls} data-testid="password-current" />
                  <input required type="password" minLength={8} value={pw.new} onChange={(e) => setPw({ ...pw, new: e.target.value })} placeholder="New password (8+ characters)" className={inputCls} data-testid="password-new" />
                  <button type="submit" className="rounded-full border border-[#C9A227] text-[#A8871B] text-sm font-semibold px-6 py-2.5 hover:bg-[#F5F3EC] transition-colors" data-testid="password-save">
                    Update password
                  </button>
                </form>
              </section>
            </div>

            <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-red-500 transition-colors" data-testid="account-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
